import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { MaskLine, easeOut } from "@/components/common/Reveal";
import { useLead } from "@/components/common/LeadModal";

const HERO_IMG =
  "https://images.pexels.com/photos/20862827/pexels-photo-20862827.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600";

export default function Hero() {
  const { openLead } = useLead();

  return (
    <section className="relative min-h-[92vh] overflow-hidden bg-brand-blue pt-20" data-testid="hero">
      {/* Background video */}
      <div className="absolute inset-0">
        <video
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_IMG}
          data-testid="hero-video"
        >
          <source src="https://videos.pexels.com/video-files/2053100/2053100-hd_1920_1080_30fps.mp4" type="video/mp4" />
          <source src="https://videos.pexels.com/video-files/3121459/3121459-uhd_2560_1440_24fps.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue via-brand-blue/85 to-brand-blue/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/70 to-transparent" />
      </div>

      <div className="relative mx-auto flex min-h-[calc(92vh-5rem)] max-w-[1400px] flex-col justify-center px-5 py-20 lg:px-8">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white backdrop-blur"
        >
          <Sparkles className="h-4 w-4 text-brand-red-light" /> Regroupement de centres de formation certifiés Qualiopi
        </motion.span>

        <h1 className="max-w-4xl font-display text-[2.4rem] font-black uppercase leading-[1.12] tracking-[-0.02em] text-white sm:text-5xl lg:text-[4.5rem]">
          <MaskLine delay={0.1}>Les formations qui vous ouvrent</MaskLine>
          <MaskLine delay={0.22}>la route vers <span className="text-brand-red-light">l'emploi</span></MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.7, ease: easeOut }}
          className="mt-7 max-w-xl text-lg leading-relaxed text-white/85"
        >
          Formations en transport de marchandises, de voyageurs et enseignement de la conduite.
          Permis, FIMO, FCO et titres professionnels, en présentiel dans nos centres d'Île-de-France.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.68, duration: 0.7, ease: easeOut }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => openLead("rappel")}
            className="rounded-full bg-brand-red px-8 py-4 text-base font-bold text-white transition-colors hover:bg-brand-red-light"
            data-testid="hero-cta-rappel"
          >
            Être rappelé
          </button>
          <Link
            to="/nos-formations"
            className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-brand-blue transition-transform hover:-translate-y-0.5"
            data-testid="hero-cta-formations"
          >
            Nos formations
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
